//@author devin
import { DataAccess } from "../dataaccess/data.access";
import { Timeout } from "../decorators/timeout.decorator";
import { NotificationPolicyMapper } from "../modules/s25-notification-policy/notification.policy.mapper";
import { NotificationPolicyModel } from "../modules/s25-notification-policy/notification.policy.model";
import { S25Util } from "../util/s25-util";
import { UserprefService } from "./userpref.service";
import { EventService } from "./event.service";
import { PayloadI } from "../pojo/EventTypeBulkEditPayloadI";
import { Task } from "../pojo/Task";

type NotifyEndpoint = {
    serviceUrl: string;
    idNodeName: string;
};

export class NotificationPolicyService {
    private static readonly notifyEndpoints: Record<NotificationPolicyModel.ObjectType, NotifyEndpoint> = {
        organization: {
            serviceUrl: "/org_notify.json",
            idNodeName: "organization_id",
        },
        location: {
            serviceUrl: "/space_notify.json",
            idNodeName: "space_id",
        },
        resource: {
            serviceUrl: "/resource_notify.json",
            idNodeName: "resource_id",
        },
        eventType: {
            serviceUrl: "/ev_type_notify.json",
            idNodeName: "type_id",
        },
        requirement: {
            serviceUrl: "/evreq_notify.json",
            idNodeName: "service_id",
        },
    };

    @Timeout
    public static async get(itemTypeId: NotificationPolicyModel.NotifyFetchItemTypeId, itemId: number) {
        if (!(await UserprefService.getLoggedIn())) {
            return Promise.resolve([]);
        }
        const endpoint = NotificationPolicyService.endpointForItemType(itemTypeId);
        return DataAccess.get(endpoint.serviceUrl + "?" + endpoint.idNodeName + "=" + itemId).then(function (data) {
            return (
                data &&
                data.notify_info &&
                data.notify_info.notification_policy &&
                data.notify_info.notification_policy.length &&
                S25Util.prettifyJson(data.notify_info.notification_policy[0], null, { contact: true })
            );
        });
    }

    public static delete(itemTypeId: NotificationPolicyModel.NotifyFetchItemTypeId, itemIds: number | number[]) {
        itemIds = S25Util.array.forceArray(itemIds) as number[];
        const endpoint = NotificationPolicyService.endpointForItemType(itemTypeId);
        return DataAccess.delete(endpoint.serviceUrl + "?" + endpoint.idNodeName + "=" + itemIds.join("+"));
    }

    public static saveObjectNotify(
        session: NotificationPolicyModel.Session,
        itemTypeId: NotificationPolicyModel.NotifyFetchItemTypeId,
        itemId: number,
    ) {
        if (NotificationPolicyMapper.isRemovedPolicy(session.draft)) {
            return NotificationPolicyService.delete(itemTypeId, itemId);
        }

        const validation = NotificationPolicyMapper.validateDraft(session.draft);
        if (!validation.valid) {
            return Promise.reject({ error: validation.message });
        }
        const draft = validation.draft;
        const respondWithin = S25Util.daysHoursMinutesToDuration({
            days: draft.days || 0,
            hours: draft.hours || 0,
            minutes: draft.minutes || 0,
        });
        const endpoint = NotificationPolicyService.endpointForItemType(itemTypeId);
        const method = session.isNewPolicy ? DataAccess.post : DataAccess.put;
        const status = session.isNewPolicy ? "new" : "mod";
        const itemIdString = String(itemId);
        const body: NotificationPolicyModel.NotifyInfoBody = S25Util.getPayload(
            "notify_info",
            "notification_policy",
            "object_id",
            status,
            itemIdString,
            {
                approval_type: draft.approvalType?.itemId ?? Task.NotificationPolicy.ApproverQuorum.Any,
                respond_within: respondWithin,
                contact: NotificationPolicyMapper.diffToWireContacts(draft, session.baseline),
            },
        );
        return method(endpoint.serviceUrl + "?" + endpoint.idNodeName + "=" + itemIdString, body);
    }

    public static replaceObjectsBulk(
        itemTypeId: NotificationPolicyModel.NotifyFetchItemTypeId,
        itemIds: number[],
        draft: NotificationPolicyModel.FormModel,
    ) {
        const validation = NotificationPolicyMapper.validateDraft(draft);
        if (!validation.valid) {
            return Promise.reject({ error: validation.message });
        }
        const validatedDraft = validation.draft;
        const respondWithin = S25Util.daysHoursMinutesToDuration({
            days: validatedDraft.days || 0,
            hours: validatedDraft.hours || 0,
            minutes: validatedDraft.minutes || 0,
        });
        return NotificationPolicyService.setBulk(
            itemTypeId,
            itemIds,
            validatedDraft.approvalType?.itemId ?? Task.NotificationPolicy.ApproverQuorum.Any,
            respondWithin,
            validatedDraft.assignments.map((assignment) =>
                NotificationPolicyMapper.assignmentToWireContact(assignment),
            ),
            [],
            true,
        );
    }

    public static addObjectBulkContacts(
        itemTypeId: NotificationPolicyModel.NotifyFetchItemTypeId,
        itemIds: number[],
        assignments: NotificationPolicyModel.Assignment[],
    ) {
        return NotificationPolicyService.setBulk(
            itemTypeId,
            itemIds,
            undefined,
            undefined,
            assignments.map((assignment) => NotificationPolicyMapper.assignmentToWireContact(assignment)),
            [],
            undefined,
        );
    }

    public static removeObjectBulkContacts(
        itemTypeId: NotificationPolicyModel.NotifyFetchItemTypeId,
        itemIds: number[],
        contactIds: number[],
    ) {
        return NotificationPolicyService.setBulk(
            itemTypeId,
            itemIds,
            undefined,
            undefined,
            [],
            contactIds.map((contactId) => ({ contact_id: contactId })),
            undefined,
        );
    }

    public static updateEventTypeBulkEdit(payload: PayloadI) {
        return EventService.updateBulkEditEventType(payload);
    }

    public static setBulk(
        itemTypeId: NotificationPolicyModel.NotifyFetchItemTypeId,
        itemIds: number[],
        approvalTypeId: number | undefined,
        respondWithin: string | undefined,
        addContacts: NotificationPolicyModel.WireContactIdentity[] | null | undefined,
        removeContacts: NotificationPolicyModel.WireContactRemove[] | null | undefined,
        deleteAllContacts: boolean | undefined,
    ) {
        const url = "/notification/policy.json?itemTypeId=" + itemTypeId;
        const date = new Date(1900, 0, 1);
        const root: NotificationPolicyModel.BulkPolicyPutRoot = {
            delete_all_contacts: deleteAllContacts ? 1 : 0,
            objects: itemIds.map((objectId) => ({ object_id: objectId })),
            contactsAdd: addContacts ?? [],
            contactsRemove: removeContacts ?? [],
        };
        if (approvalTypeId != null) {
            root.approval_type = approvalTypeId;
        }
        if (respondWithin) {
            root.respond_within = S25Util.date.dropTZString(
                S25Util.date.addMinutes(date, S25Util.ISODurationToMinutes(respondWithin)),
            );
        }
        const body: NotificationPolicyModel.BulkPolicyEnvelope<NotificationPolicyModel.BulkPolicyPutRoot> = { root };
        return DataAccess.put(url, body);
    }

    public static copy(itemTypeId: NotificationPolicyModel.NotifyFetchItemTypeId, srcId: number, dstId: number) {
        return NotificationPolicyService.get(itemTypeId, srcId).then((resp) => {
            if (!resp) {
                return;
            }
            const draft = NotificationPolicyMapper.draftFromGetResponse(resp);
            const session: NotificationPolicyModel.Session = {
                draft,
                baseline: null,
                isNewPolicy: true,
            };
            return NotificationPolicyService.saveObjectNotify(session, itemTypeId, dstId);
        });
    }

    public static deleteBulk(itemTypeId: NotificationPolicyModel.NotifyFetchItemTypeId, itemIds: number[]) {
        const url = "/notification/policy.json?itemTypeId=" + itemTypeId;
        const root: NotificationPolicyModel.BulkPolicyDeleteRoot = {
            approval_type: 0,
            objects: itemIds.map((objectId) => ({ object_id: objectId })),
        };
        const body: NotificationPolicyModel.BulkPolicyEnvelope<NotificationPolicyModel.BulkPolicyDeleteRoot> = {
            root,
        };
        return DataAccess.delete(url, { data: body });
    }

    public static updateNotifyTypeOnly(
        itemTypeId: NotificationPolicyModel.NotifyFetchItemTypeId,
        itemIds: number[],
        notifyTypeOnly: number,
        notifyType: number, // Id for object
    ) {
        const url =
            "/notification/policy.json?itemTypeId=" +
            itemTypeId +
            "&isNotifyTypeOnly=" +
            notifyTypeOnly +
            "&notifyType=" +
            notifyType;
        const root: NotificationPolicyModel.BulkPolicyPutRoot = {
            delete_all_contacts: 0,
            objects: itemIds.map((objectId) => ({ object_id: objectId })),
            contactsAdd: [],
            contactsRemove: [],
        };
        const body: NotificationPolicyModel.BulkPolicyEnvelope<NotificationPolicyModel.BulkPolicyPutRoot> = { root };
        return DataAccess.put(url, body);
    }

    private static endpointForItemType(itemTypeId: NotificationPolicyModel.NotifyFetchItemTypeId): NotifyEndpoint {
        const objectType = NotificationPolicyModel.objectTypeFromNotifyItemTypeId(itemTypeId);
        if (!objectType) {
            throw new Error("Unknown notification policy item type " + itemTypeId);
        }
        return NotificationPolicyService.notifyEndpoints[objectType];
    }
}

// {
//     delete_all_contacts: 1|0
//     approval_type: ###
//     respond_within: datetime, <-- this is based off of 1900-01-01THH:MM:SS so perhaps just send in the date like that
//     objects: [
//        { object_id: ### }, { object_id: ### }, ...
//     ],
//     contactsAdd: [
//        { contact_id: ###, notify_type: ### }, { contact_id: ###, notify_type: ### }, ...
//     ],
//     contactsRemove: [
//        { contact_id: ### }, { contact_id: ### }, ...
//     ],
//  }
